import { useBreakpoint } from '@context/DeviceBreakpointContext/DeviceBreakpointContext';
import { WinePageContext, WinePageProvider } from '@context/WinePageContext/WinePageContext';
import { isSSR } from '@helpers/ssr';
import { getWineName } from '@helpers/wine';
import {
  ALL_REVIEWS_ID,
  Anchor,
  UserProvider,
  VivinoRating,
  VivinoRatingSizes,
} from '@vivino/js-web-common';
import { BasicVintage, FullVintage, Highlights } from '@webtypes/goApi';
import React, { useContext, useEffect } from 'react';
import Layout from 'vivino-js/utilities/Layout/Layout';
import Page from 'vivino-js/utilities/Page/Page';
import DiscountPercentLabel from 'vivino-ui/atoms/DiscountPercentLabel/DiscountPercentLabel';

import { findMainCellar } from '@lib/cellar';

import { CellarContextProvider } from '@components/Cellar/CellarContext/CellarContext';
import ContentfulPromoBanner from '@components/ContentfulPromoBanner/ContentfulPromoBanner';
import Price from '@components/Price/Price';
import UserVintageActionButtons from '@components/UserVintageActionButtons/UserVintageActionButtons';
import WineHeadline from '@components/WineHeadline/WineHeadline';

import WineLabel from './components/WineLabel/WineLabel';
import BreadCrumbs from 'vivino-ui/components/BreadCrumbs';
import ExternalAd, { ExternalPromotion, loadAds } from 'vivino-ui/components/ExternalAd/ExternalAd';
import Highlight from 'vivino-ui/components/Highlight/Highlight.presentational';

import PurchaseAvailability from '../purchaseAvailability';
import styles from './topSection.module.scss';
import { injectCampaignCardButtons } from './utils/injectCampaignCardButtons';

interface Props {
  user: any;
  userCellars: any;
  translations: any;
  stateCode: string;
  pageInformation: any;
  winePageType: string;
  countryCode: string;
  vintage: BasicVintage | FullVintage;
  highlights: Highlights;
  isMobile: boolean;
  label: any;
  promotion: ExternalPromotion;
}

const WinePageTopSection = (props: Props) => {
  return (
    <Page {...props}>
      <WinePageProvider pageInformation={props.pageInformation}>
        <App {...props} />
      </WinePageProvider>
    </Page>
  );
};

const App = ({
  user,
  userCellars,
  highlights,
  winePageType,
  countryCode,
  stateCode,
  label,
  promotion,
}: Props) => {
  const cellar = findMainCellar({ userCellars });
  const { isMobile } = useBreakpoint();
  const { pageInformation } = useContext(WinePageContext);

  const vintage = pageInformation?.vintage;
  const priceAndAvailability = pageInformation?.prices_and_availability;
  const priceId = priceAndAvailability?.availability?.price?.id;
  const merchantsWithPrices = priceAndAvailability?.merchants_with_prices;
  const merchantPrice = merchantsWithPrices
    ?.flatMap((merchant) => merchant?.prices || [])
    .find((price) => price?.id === priceId);

  const hasPerfectLabel = !!vintage?.image?.variations?.bottle_medium;

  useEffect(() => {
    loadAds();
    if (!isSSR && merchantPrice) {
      injectCampaignCardButtons();
    }
  }, []);

  return (
    <Layout className={styles.layout}>
      <UserProvider user={user}>
        <CellarContextProvider cellarName={cellar?.name} cellarId={cellar?.id}>
          <div className={styles.container}>
            <div className={styles.leftFlank}>
              <div className={styles.bottleShot}>
                <WineLabel
                  label={label}
                  altText={getWineName(vintage.wine)}
                  hasPerfectLabel={hasPerfectLabel}
                />
              </div>
              <div className={styles.textContainer}>
                <div className={styles.headlineTop}>
                  {!isMobile && <WineHeadline vintage={vintage} />}
                </div>
                <div>
                  <div className={styles.breadCrumbsLocationHeader}>
                    <div className={styles.breadcrumbsDesktop}>
                      <BreadCrumbs vintage={vintage} items={[]} />
                    </div>
                    <div className={styles.ratings}>
                      <Anchor href={`#${ALL_REVIEWS_ID}`} className={styles.scrollLink}>
                        <VivinoRating
                          vintage={vintage}
                          size={isMobile ? VivinoRatingSizes.Large : null}
                        />
                      </Anchor>
                    </div>
                    {isMobile && merchantPrice && (
                      <div className={styles.prices}>
                        <div className={styles.discounts}>
                          <Price
                            className={styles.currentPrice}
                            amount={merchantPrice.amount}
                            currency={merchantPrice.currency}
                          />
                          {merchantPrice?.discounted_from && (
                            <Price
                              strike
                              className={styles.retailPrice}
                              amount={merchantPrice?.discounted_from}
                              currency={merchantPrice.currency}
                              dataTestId="discountPrice"
                            />
                          )}
                        </div>
                        {!isSSR && (
                          <DiscountPercentLabel price={merchantPrice} className={styles.save} />
                        )}
                      </div>
                    )}
                    {!isMobile && (
                      <UserVintageActionButtons vintage={vintage} isLoggedIn={!!user?.id} />
                    )}
                  </div>
                </div>
                {!isMobile && (
                  <div className={styles.highlightsDesktop}>
                    {highlights.map((highlight) => (
                      <Highlight key={highlight.highlight_type} highlight={highlight} />
                    ))}
                  </div>
                )}
              </div>
            </div>
            {isMobile && (
              <div className={styles.headlineMobile}>
                <WineHeadline vintage={vintage} />
                <BreadCrumbs vintage={vintage} items={[]} />
              </div>
            )}
            <div className={styles.rightFlank}>
              <PurchaseAvailability
                countryCode={countryCode}
                stateCode={stateCode}
                winePageType={winePageType}
                pageInformation={pageInformation}
              />
            </div>
            {isMobile && (
              <div className={styles.highlightsMobile}>
                <UserVintageActionButtons vintage={vintage} isLoggedIn={!!user?.id} />
                <div className={styles.highlights}>
                  {highlights.map((highlight) => (
                    <Highlight key={highlight.highlight_type} highlight={highlight} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={styles.topSectionAd}>
            {promotion?.type === 'contentful_promo_banner' ? (
              <ContentfulPromoBanner promotion={promotion} vintage={vintage} />
            ) : (
              <ExternalAd
                dataAdSlot={promotion?.external?.ad_unit_id}
                divId={promotion?.external?.div_id}
              />
            )}
          </div>
        </CellarContextProvider>
      </UserProvider>
    </Layout>
  );
};

export default WinePageTopSection;
